import { FileData, ModelData, useRegisterAssistant } from '@/hooks/useRegisterAssistant';
import {
  ChevronRight,
  Plus,
  Save,
  Upload,
  X,
} from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { useConfigAssistant } from '@/hooks/useConfigAssistant';
import { useEditAssistant } from '@/hooks/useEditAssistant';
import { IAssistantsList } from '@/hooks/useListAssistants';
import { AddModelTabs } from './Tabs';
import { ToolSettingsEditor } from './ToolSettingsEditor';
import api from '@/services/api';

interface FoundryAgentOption {
  id: string;
  name: string;
}

interface ServerFileData {
  entity_type: 'file' | 'queue_item';
  set: string;
  id: string;
  file_name: string;
  blob_name?: string;
  description?: string;
  _rid?: string;
  _self?: string;
  _etag?: string;
  _attachments?: string;
  _ts?: number;
}

interface FormErrors {
  name?: string;
  description?: string;
  foundry_agent?: string;
  suggested_prompts?: string;
  tools?: string;
  mcp_servers?: string;
  files?: string;
  model?: string;
  [key: string]: string | undefined;
}

const AssistantForm: React.FC<{
  onSubmit: (formData?: ModelData) => void;
  onCancel: () => void;
  itemSelected: IAssistantsList | null;
}> = ({ onSubmit, onCancel, itemSelected }) => {
  const { availableTools, availableModels } = useConfigAssistant({ auto: true });
  const { registerAssistant } = useRegisterAssistant();
  const { editAssistant } = useEditAssistant();

  // Helper function for input styles
  const getInputStyles = () => ({
    borderColor: "hsl(var(--muted-foreground))",
  });

  const handleInputFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    e.target.style.borderColor = "hsl(var(--primary))";
    e.target.style.boxShadow = "0 0 0 2px hsl(var(--primary) / 0.2)";
  };

  const handleInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    e.target.style.borderColor = "hsl(var(--muted-foreground))";
    e.target.style.boxShadow = 'none';
  };

  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(!!itemSelected);

  const [currentEditItem, setCurrentEditItem] = useState<IAssistantsList | null>(itemSelected);

  const [activeTab, setActiveTab] = useState<string>('basic');

  const [formData, setFormData] = useState<ModelData>({
    name: '',
    description: '',
    suggested_prompts: [''],
    tools: [''],
    mcp_servers: [],
    files: [],
    order: 0,
    model: '',
    tool_settings: {},
    custom_data: {},
  });

  const [foundryAgents, setFoundryAgents] = useState<FoundryAgentOption[]>([]);
  const fetchFoundryAgents = useCallback(async () => {
    try {
      const response = await api.get<FoundryAgentOption[]>('/api/v1/assistants/foundry-agents');
      setFoundryAgents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching foundry agents:', err);
      setFoundryAgents([]);
    }
  }, []);
  useEffect(() => {
    fetchFoundryAgents();
  }, [fetchFoundryAgents]);

  const [serverFiles, setServerFiles] = useState<ServerFileData[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const logoUploadRef = useRef<HTMLInputElement>(null);

  const openPanel = (): void => {
    resetForm();
    setCurrentEditItem(null);
    setIsPanelOpen(true);
  };

  const resetForm = (): void => {
    setFormData({
      name: '',
      description: '',
      suggested_prompts: [''],
      tools: [''],
      mcp_servers: [],
      files: [],
      order: 0,
      model: '',
      tool_settings: {},
      custom_data: {},
    });
    setServerFiles([]);
    setErrors({});
    setActiveTab('basic');
  };

  const closePanel = (): void => {
    setIsPanelOpen(false);

    setTimeout(() => {
      if (!currentEditItem) {
        resetForm();
      }
    }, 300);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleSuggestedPromptChange = (index: number, value: string): void => {
    const updatedPrompts = [...formData.suggested_prompts];
    updatedPrompts[index] = value;

    setFormData({
      ...formData,
      suggested_prompts: updatedPrompts,
    });
  };

  const addSuggestedPrompt = (): void => {
    setFormData({
      ...formData,
      suggested_prompts: [...formData.suggested_prompts, ''],
    });
  };

  const removeSuggestedPrompt = (index: number): void => {
    const updatedPrompts = formData.suggested_prompts.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      suggested_prompts: updatedPrompts,
    });
  };

  const handleToolToggle = (toolName: string): void => {
    setFormData((prevFormData) => {
      const currentTools = prevFormData.tools || [];
      if (currentTools.includes(toolName)) {
        // Remove tool and its settings
        const updatedToolSettings = { ...prevFormData.tool_settings };
        delete updatedToolSettings[toolName];
        return {
          ...prevFormData,
          tools: currentTools.filter((tool) => tool !== toolName),
          tool_settings: updatedToolSettings,
        };
      } else {
        return {
          ...prevFormData,
          tools: [...currentTools, toolName],
          tool_settings: {
            ...prevFormData.tool_settings,
            [toolName]: {},
          },
        };
      }
    });
  };

  const handleToolSettingsChange = (settings: Record<string, Record<string, unknown>>): void => {
    setFormData({
      ...formData,
      tool_settings: settings,
    });
  };

  const handleFoundryAgentChange = (agentId: string): void => {
    setFormData((prev) => ({
      ...prev,
      custom_data: {
        ...prev.custom_data,
        foundry_agent: agentId || undefined,
      },
    }));
    if (errors.foundry_agent) {
      setErrors((prev) => ({ ...prev, foundry_agent: undefined }));
    }
  };


  const triggerLogoUpload = (): void => {
    if (logoUploadRef.current) {
      logoUploadRef.current.click();
    }
  };

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    new Promise<FileData>((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = (e.target?.result as string) || '';
        const base64Data = base64.split(',')[1] || base64;
        resolve({ name: file.name, data: base64Data });
      };
      reader.readAsDataURL(file);
    })
      .then((newLogo) => {
        setFormData({
          ...formData,
          logo_file: newLogo,
        });

        toast.success(`Logo file added successfully`);
      })
      .catch((error) => {
        console.error('Error processing logo file:', error);
        toast.error('Error logo file. Please try again.');
      });

    if (logoUploadRef.current) {
      logoUploadRef.current.value = '';
    }
  };

  // Form validation and submission
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    const foundryAgentId = formData.custom_data?.foundry_agent?.trim();
    if (!foundryAgentId) {
      newErrors.foundry_agent = 'Foundry Agent is required';
    }

    const emptyPromptIndexes = formData.suggested_prompts
      .map((prompt, index) => (prompt.trim() === '' ? index : null))
      .filter((index): index is number => index !== null);

    if (emptyPromptIndexes.length > 0) {
      newErrors.suggested_prompts = 'All suggested prompts must have content';
    }

    if (formData.tools && formData.tools.length > 0 && formData.tools[0] !== '') {
      const emptyToolIndexes = formData.tools
        .map((tool, index) => (tool.trim() === '' ? index : null))
        .filter((index): index is number => index !== null);

      if (emptyToolIndexes.length > 0) {
        newErrors.tools = 'All tools must have content';
      }
    }

    if (formData.mcp_servers && formData.mcp_servers.length > 0 && formData.mcp_servers[0] !== null) {
      const emptyServerIndexes = formData.mcp_servers
        .map((server, index) => (server === null ? index : null))
        .filter((index): index is number => index !== null);

      if (emptyServerIndexes.length > 0) {
        newErrors.mcp_servers = 'All MCP servers must have content';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = (): void => {
    // Clear the current edit item
    setCurrentEditItem(null);
    closePanel();
    onCancel();
  };

  const handleSubmit = async (): Promise<void> => {
    if (validateForm()) {
      const cleanedFormData = {
        ...formData,
        tools: formData.tools?.filter((tool) => tool.trim() !== '') || [],
        mcp_servers: formData.mcp_servers || [],
        existingFiles: serverFiles,
      };

      const toastId = toast.loading(
        currentEditItem ? 'Updating assistant...' : 'Creating new assistant...',
      );

      try {
        if (currentEditItem) {
          await editAssistant(currentEditItem.id, cleanedFormData);
          onSubmit(cleanedFormData);
          toast.success('Assistant updated successfully', { id: toastId });
        } else {
          await registerAssistant(cleanedFormData);
          onSubmit(cleanedFormData);
          toast.success('Assistant created successfully', { id: toastId });
        }

        setCurrentEditItem(null);
        closePanel();
      } catch (error) {
        console.error('Error saving assistant:', error);

        toast.error(
          currentEditItem
            ? 'Error updating assistant. Please try again.'
            : 'Error creating assistant. Please try again.',
          { id: toastId },
        );
      }
    }
  };

  useEffect(() => {
    if (itemSelected) {
      setCurrentEditItem(itemSelected);

      setIsPanelOpen(true);

      const updatedItemSelected: ModelData = {
        ...itemSelected,
        suggested_prompts: itemSelected.suggested_prompts || [''],
        tools: itemSelected.tools || [''],
        mcp_servers: itemSelected.mcp_servers || [],
        files: [],
        order: itemSelected.order || 0,
        model: itemSelected.model || '',
        tool_settings: itemSelected.tool_settings || {},
        custom_data: itemSelected.custom_data || {},
      };

      if (itemSelected.files && Array.isArray(itemSelected.files)) {
        const serverFilesList = itemSelected.files.filter(
          (file) => 'entity_type' in file,
        ) as unknown as ServerFileData[];
        setServerFiles(serverFilesList);
      }

      setFormData(updatedItemSelected);
      setActiveTab('basic');
    }
  }, [itemSelected]);

  return (
    <div>
      <button
        onClick={openPanel}
        className="font-medium py-2 px-4 rounded-lg flex items-center transition-colors"
        style={{
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "hsl(var(--secondary))";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
        }}
      >
        <Plus size={18} className="mr-2" />
        Add Assistant
      </button>

      {isPanelOpen && (
        <div
          className="fixed inset-0 transition-opacity duration-300 z-40"
          style={{
            backgroundColor: "hsl(var(--foreground) / 0.5)",
          }}
          onClick={closePanel}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed inset-y-0 right-0 w-full md:w-2/3 lg:w-1/2 shadow-xl transform transition-transform duration-300 ease-out z-50 bg-background ${isPanelOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          <div
            className="flex items-center justify-between border-b px-6 py-4"
            style={{ borderColor: "hsl(var(--muted-foreground))" }}
          >
            <div className="flex items-center">
              <button
                onClick={handleCancel}
                className="mr-4 focus:outline-none text-muted-foreground"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "hsl(var(--foreground))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "hsl(var(--muted-foreground))";
                }}
                aria-label="Back"
              >
                <ChevronRight size={24} />
              </button>
              <h2 className="text-xl font-medium text-foreground">
                {itemSelected ? `Edit Assistant: ${itemSelected.name}` : 'Add New Assistant'}
              </h2>
            </div>
            <button
              onClick={handleCancel}
              className="focus:outline-none text-muted-foreground"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "hsl(var(--foreground))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "hsl(var(--muted-foreground))";
              }}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          {/* Tabs Navigation */}
          <AddModelTabs activeTab={activeTab} setActiveTab={setActiveTab} />

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div>
                  <label
                    className="block text-sm font-medium mb-1 text-foreground"
                  >
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg outline-none transition-colors ${errors.name ? 'border-red-500' : ''
                      }`}
                    style={{
                      borderColor: errors.name ? '#ef4444' : "hsl(var(--muted-foreground))",
                    }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Enter assistant name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label
                    className="block text-sm font-medium  mb-1 text-foreground"
                  >
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                    style={getInputStyles()}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Describe the assistant"
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1 text-foreground"
                  >
                    Logo
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={logoUploadRef}
                    onChange={handleLogoUpload}
                    className="hidden"
                  />

                  {(formData.logo || formData.logo_file) && (
                    <div className="flex flex-col items-center justify-center mb-4">
                      <p
                        className="text-sm text-neutral-2 font-medium mb-2 text-foreground"
                      >
                        {formData.logo_file
                          ? 'New logo preview:'
                          : `Current logo: ${formData.logo}`}
                      </p>
                      <div className="relative">
                        {formData.logo_file ? (
                          <img
                            key={`new-logo-${Date.now()}`}
                            src={`data:image/*;base64,${formData.logo_file.data}`}
                            alt="New Logo Preview"
                            className="max-w-32 max-h-32 object-contain rounded-lg border border-neutral-1"
                            onError={(e) => {
                              e.currentTarget.src = '/images/Blue_IAC_Logo.png';
                            }}
                          />
                        ) : (
                          <img
                            key={`${itemSelected?.id}-${formData.logo}-${Date.now()}`}
                            src={`${import.meta.env.VITE_BACKEND_URL}/api/v1/assistants/${itemSelected?.id || 'preview'}/logo?t=${Date.now()}`}
                            alt="Current Logo"
                            className="max-w-32 max-h-32 object-contain rounded-lg border border-neutral-1"
                            onError={(e) => {
                              e.currentTarget.src = '/images/Blue_IAC_Logo.png';
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={triggerLogoUpload}
                    className="w-full border-2 border-dashed border-border p-6 rounded-lg flex flex-col items-center justify-center hover:bg-muted/50 transition-colors mb-6"
                  >
                    <Upload size={32} className="mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-medium">Click to upload an image</p>
                  </button>

                  {errors.logo && <p className="mt-1 text-sm text-red-600">{errors.logo}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      className="block text-sm font-medium mb-1 text-foreground"
                    >
                      Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                      style={getInputStyles()}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1 text-foreground"
                    >
                      Model
                    </label>
                    <select
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                      style={getInputStyles()}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    >
                      {availableModels?.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'prompt' && (
              <div>
                <label
                  className="block text-sm font-medium mb-1 text-foreground"
                  htmlFor="foundry-agent-select"
                >
                  Foundry Agent <span className="text-red-500">*</span>
                </label>
                <select
                  id="foundry-agent-select"
                  name="foundry_agent"
                  value={formData.custom_data?.foundry_agent ?? ''}
                  onChange={(e) => handleFoundryAgentChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg outline-none transition-colors ${errors.foundry_agent ? 'border-red-500' : ''}`}
                  style={{
                    borderColor: errors.foundry_agent ? '#ef4444' : "hsl(var(--muted-foreground))",
                  }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  aria-describedby={errors.foundry_agent ? 'foundry-agent-error' : undefined}
                >
                  <option value="">Select a Foundry Agent</option>
                  {foundryAgents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                {errors.foundry_agent && (
                  <p id="foundry-agent-error" className="mt-1 text-sm text-red-600">
                    {errors.foundry_agent}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'suggested' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label
                    className="block text-sm font-medium text-foreground"
                  >
                    Suggested Prompts <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addSuggestedPrompt}
                    className="text-sm font-medium flex items-center transition-colors text-foreground"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "hsl(var(--foreground))";
                    }}
                  >
                    <Plus size={16} className="mr-1" /> Add Prompt
                  </button>
                </div>

                {errors.suggested_prompts && (
                  <p className="mt-1 text-sm text-red-600 mb-2">{errors.suggested_prompts}</p>
                )}

                <div className="space-y-3">
                  {formData.suggested_prompts.map((prompt, index) => (
                    <div key={`prompt-${index}`} className="flex items-center">
                      <input
                        type="text"
                        value={prompt}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleSuggestedPromptChange(index, e.target.value)
                        }
                        className="flex-1 px-3 py-2 border rounded-lg outline-none transition-colors"
                        style={getInputStyles()}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={`Suggested prompt ${index + 1}`}
                      />
                      {formData.suggested_prompts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSuggestedPrompt(index)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tools & Servers Tab */}
            {activeTab === 'tools' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-sm font-medium mb-3 text-foreground">
                    Available Tools
                  </h3>
                  <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
                    {availableTools?.map((tool) => (
                      <div key={tool.id} className="flex flex-row gap-2 items-start">
                        <Checkbox
                          className="h-4 w-4 mt-0.5 text-primary"
                          checked={formData.tools?.includes(tool.id) || tool.global || false}
                          onCheckedChange={() => handleToolToggle(tool.id)}
                          disabled={tool.global}
                        />
                        <label className="text-sm font-medium text-primary">
                          {tool.name}
                        </label>
                      </div>
                    ))}
                  </div>

                  {errors.tools && <p className="mt-2 text-sm text-red-600">{errors.tools}</p>}
                </div>

                {/* Tool Settings Editor */}
                <ToolSettingsEditor
                  selectedTools={(formData.tools || []).filter((tool) => tool !== '').concat(availableTools?.filter((tool) => tool.global).map((tool) => tool.id) || [])}
                  toolSettings={formData.tool_settings || {}}
                  onSettingsChange={handleToolSettingsChange}
                />
              </div>
            )}

          </div>

          <div className="border-t p-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted/10 transition-colors"
              style={{
                color: "hsl(var(--foreground))",
                borderColor: "hsl(var(--muted-foreground))",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 rounded-lg flex items-center transition-colors border"
              style={{
                color: "hsl(var(--primary))",
                borderColor: "hsl(var(--primary))",
              }}
            >
              <Save size={18} className="mr-2" />
              {itemSelected ? 'Update Assistant' : 'Save Assistant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantForm;
